/**
 * Presentation owner for the two procedural fighter rigs.
 *
 * Two character variants are built for each slot during init. Character swaps performed by the
 * capture harness therefore only toggle visibility and never allocate inside update.
 */

import { DisposalBag, System } from '../core/index.js';
import { ROSTER_IDS, getStats } from '../combat/index.js';
import { createFighterVisual, poseFighterVisual } from './procedural-fighter.js';

export class FighterVisualSystem extends System {
  static id = 'fighters-visual';
  // Render owns scene creation. All systems update before any lateUpdate, so these poses still
  // reach the same frame that RenderSystem draws.
  static deps = ['render', 'materials', 'animation'];
  static budgetMs = 0.45;

  constructor() {
    super();
    this.bag = new DisposalBag('fighters-visual');
    this.slots = [new Map(), new Map()];
    this.active = [null, null];
    this.scene = null;
    this.animation = null;
  }

  async init(ctx) {
    this.scene = ctx.render.scene;
    if (!this.scene) throw new Error('fighters-visual: render scene is unavailable.');
    const materials = ctx.systems.get('materials');
    this.animation = ctx.systems.get('animation');
    for (let slot = 0; slot < 2; slot++) {
      for (const id of ROSTER_IDS) {
        const visual = createFighterVisual(getStats(id), this.bag, materials);
        visual.visible = false;
        this.scene.add(visual);
        this.slots[slot].set(id, visual);
      }
    }
    this.resetVisuals(ctx);
    ctx.log.info('fighters: procedural Kestrel and Bulwark rigs ready (2 slot variants each)');
  }

  update(dt, ctx) {
    const alpha = ctx.time.alpha;
    for (let slot = 0; slot < 2; slot++) {
      const f = ctx.sim.fighters[slot];
      if (!f) continue;
      let visual = this.slots[slot].get(f.id);
      if (!visual) visual = this.slots[slot].get('kestrel');
      if (this.active[slot] !== visual) {
        for (const candidate of this.slots[slot].values()) candidate.visible = candidate === visual;
        this.active[slot] = visual;
      }
      const motion = f.move ? this.animation.profile(f.move.anim) : null;
      const stateMotion = this.animation.stateProfile(f);
      const opponent = ctx.sim.fighters[1 - slot];
      const throwOwner = f.throwMoveId ? f : opponent && opponent.throwMoveId ? opponent : null;
      let throwMotion = f.throwPartner >= 0 && throwOwner
        ? this.animation.throwProfile(throwOwner.throwMoveId)
        : null;
      // A completed throw deliberately clears moveId/throwMoveId. The already-authoritative
      // action log retains the completed id, allowing a short release follow-through without
      // presentation cache or screenshot-order-dependent pose history.
      if (!throwMotion && f.prevState === 'throw-execute' && f.stateTick <= 60) {
        const length = f.actionLog.length;
        const recent = f.actionLog[(f.actionLogHead - 1 + length) % length];
        if (recent && recent.moveId) throwMotion = this.animation.throwProfile(recent.moveId);
      }
      poseFighterVisual(
        visual, f, alpha, ctx.time.simSeconds, opponent, motion, stateMotion, throwMotion,
      );
    }
    void dt;
  }

  resetVisuals(ctx) {
    this.active[0] = null;
    this.active[1] = null;
    this.update(0, ctx);
  }

  dispose() {
    if (this.scene) {
      for (const slot of this.slots) {
        for (const visual of slot.values()) this.scene.remove(visual);
      }
    }
    this.slots[0].clear();
    this.slots[1].clear();
    this.active[0] = null;
    this.active[1] = null;
    this.bag.disposeAll();
    this.scene = null;
    this.animation = null;
  }
}
